import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import EarnKaroCard from "@/components/EarnKaroCard";

export function indiaCatalogQuery(department = "", shelf = "", sort = "", category = "", sub = "", store = "", brand = "") {
  return {
    queryKey: ["india-storefront", shelf || department || "home", category || "", sub || "", store || "", brand || "", sort || "featured"],
    staleTime: 20_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = {};
      if (category) params.category = category;
      else if (shelf) params.shelf = shelf;
      else if (department) params.department = department;
      if (sub) params.sub = sub;
      if (store) params.store = store;
      if (brand) params.brand = brand;
      if (sort === "price_asc" || sort === "price_desc") params.sort = sort;
      return (await api.get("/shop/india", { params })).data;
    },
  };
}

function ProductGrid({ products }) {
  return (
    <div className="grid grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {products.map((product) => <EarnKaroCard key={product.id} product={product} />)}
    </div>
  );
}

function LoadMore({ onClick, loading, testId }) {
  return (
    <div className="mt-8 text-center">
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        data-testid={testId}
        className="text-sm tracking-luxe uppercase border border-aura-border px-8 py-3 hover:border-aura-accent disabled:opacity-50"
      >
        {loading ? "Loading…" : "Load more"}
      </button>
    </div>
  );
}

function mergeProducts(current, incoming) {
  const seen = new Set(current.map((product) => product.id));
  return [...current, ...(incoming || []).filter((product) => product?.id && !seen.has(product.id))];
}

async function nextPage({ department, shelf, offset, limit = 48, sort = "", category = "", sub = "", store = "", brand = "" }) {
  const params = { offset, limit };
  if (category) params.category = category;
  else if (shelf) params.shelf = shelf;
  else if (department) params.department = department;
  if (sub) params.sub = sub;
  if (store) params.store = store;
  if (brand) params.brand = brand;
  if (sort === "price_asc" || sort === "price_desc") params.sort = sort;
  return (await api.get("/shop/india", { params })).data;
}

function IndiaShelf({ row, sort = "" }) {
  const [extra, setExtra] = useState([]);
  const [nextOffset, setNextOffset] = useState(row.next_offset ?? null);
  const [loadingMore, setLoadingMore] = useState(false);
  const anchor = row.products?.[0]?.id || "";

  useEffect(() => {
    setExtra([]);
    setNextOffset(row.next_offset ?? null);
  }, [row.id, row.count, anchor, row.next_offset, sort]);

  const products = mergeProducts(row.products || [], extra);
  const loadMore = async () => {
    if (nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await nextPage({ department: row.id, offset: nextOffset, sort });
      setExtra((prev) => mergeProducts(prev, page.products));
      setNextOffset(page.next_offset ?? null);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section data-testid={`india-shelf-${row.id}`}>
      <h2 className="mb-4 font-serif text-2xl">{row.label}</h2>
      <ProductGrid products={products} />
      {nextOffset != null ? (
        <LoadMore onClick={loadMore} loading={loadingMore} testId={`india-load-more-${row.id}`} />
      ) : null}
    </section>
  );
}

function DepartmentGrid({ department, shelf, data, sort = "", category = "", sub = "", store = "", brand = "" }) {
  const [extra, setExtra] = useState([]);
  const [nextOffset, setNextOffset] = useState(data?.next_offset ?? null);
  const [loadingMore, setLoadingMore] = useState(false);
  const anchor = data?.products?.[0]?.id || "";

  useEffect(() => {
    setExtra([]);
    setNextOffset(data?.next_offset ?? null);
  }, [department, shelf, category, sub, store, brand, data?.total, anchor, data?.next_offset, sort]);

  const products = mergeProducts(data?.products || [], extra);
  const loadMore = async () => {
    if (nextOffset == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await nextPage({ department, shelf, offset: nextOffset, limit: 120, sort, category, sub, store, brand });
      setExtra((prev) => mergeProducts(prev, page.products));
      setNextOffset(page.next_offset ?? null);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section data-testid="india-department-grid">
      <ProductGrid products={products} />
      {products.length === 0 ? (
        <p className="py-16 text-center text-aura-muted">Nothing in this department yet.</p>
      ) : null}
      {nextOffset != null ? (
        <LoadMore onClick={loadMore} loading={loadingMore} testId="india-load-more" />
      ) : null}
    </section>
  );
}

export default function IndiaStorefront({ department = "", shelf = "", sort = "", category = "", sub = "", store = "", brand = "" }) {
  const { data, isLoading, isError, isPlaceholderData } = useQuery(
    indiaCatalogQuery(department, shelf, sort, category, sub, store, brand),
  );
  const filtered = Boolean(category || sub || store || brand);

  if (isLoading) {
    return (
      <div data-testid="india-storefront-loading" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] shimmer" />)}
      </div>
    );
  }

  if (isError || !data) {
    return <p className="py-16 text-center text-aura-muted" data-testid="india-storefront-error">The India shop could not load. Try again in a moment.</p>;
  }

  if (department || shelf || filtered) {
    if (isPlaceholderData) {
      return (
        <div data-testid="india-storefront-loading" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] shimmer" />)}
        </div>
      );
    }
    return (
      <DepartmentGrid
        department={department}
        shelf={shelf}
        data={data}
        sort={sort}
        category={category}
        sub={sub}
        store={store}
        brand={brand}
      />
    );
  }

  return (
    <div data-testid="india-storefront" className="space-y-12">
      {(data.shelves || []).map((row) => <IndiaShelf key={row.id} row={row} sort={sort} />)}
    </div>
  );
}
